import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { v4 } from 'uuid';
import { Helmet } from 'react-helmet';
import { DB } from '~/services/firebase';

import Dashboard from '~/layouts/Dashboard';
import getValidationErrors from '~/utils/getValidationErrors';
import { useToast } from '~/hooks/toast';
import IClient from '~/interfaces/IClient';
import IAddress from '~/interfaces/IAddress';

import viacep from '~/services/viacep';

interface Params {
  id: string;
  item: string;
}

const ClientAddressForm: React.FC = () => {
  const history = useHistory();
  const params = useParams<Params>();
  const { addToast } = useToast();
  const [client, setClient] = useState({} as IClient);
  const [data, setData] = useState({} as IAddress);
  const [errors, setErrors] = useState({} as any);
  const [loading, setLoading] = useState(false);
  const [index, setIndex] = useState(-1);
  const [updateScript, setUpdateScript] = useState(false);
  const { id, item: itemId } = params;
  const [address, setAddress] = useState('');

  useEffect(() => {
    async function loadItem() {
      const docSnap = await getDoc(doc(DB, 'clients', id));
      const item = docSnap.data() as IClient;

      setClient({
        ...item,
        uid: id,
      } as IClient);

      setAddress(item.address);

      if (itemId) {
        const findItem = item.addresses.findIndex(find => find.uid === itemId);

        if (findItem !== -1) {
          setIndex(findItem);
          setData(item.addresses[findItem]);
        } else {
          history.goBack();
        }
      } else {
        setData({
          number: '',
          complement: '',
          reference: '',
        } as IAddress);
      }
    }

    if (id) loadItem();
    else history.goBack();

    setTimeout(() => setUpdateScript(true), 100);
  }, [id, history, itemId]);

  const handleChangePostcode = useCallback(
    async (event: any) => {
      try {
        const { value } = event.target;

        setData({ ...data, postcode: value });

        if (value.length === 9) {
          const response = await viacep.get(`${value}/json`);
          const { logradouro, complemento, bairro, localidade, uf } =
            response.data;

          setData(current => ({
            ...current,
            road: logradouro,
            neighbourhood: bairro,
            city: localidade,
            state: uf,
            complement: complemento,
          }));
        }
      } catch (err) {
        console.log(err);
      }
    },
    [data],
  );

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        if (!address) return;
        setLoading(true);

        await updateDoc(doc(DB, 'clients', id), {
          address,
        });

        addToast({
          type: 'success',
          description: data.uid
            ? 'Endereço alterado com sucesso!'
            : 'Endereço cadastrado com sucesso!',
        });

        history.push(`/clients`);

        setLoading(false);
      } catch (err: any) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
        }
      }
    },
    [data, addToast, history, id, address],
  );

  // const handleSubmit = useCallback(
  //   async event => {
  //     event.preventDefault();

  //     try {
  //       const schema = Yup.object().shape({
  //         postcode: Yup.string().required('O CEP é obrigatório'),
  //         road: Yup.string().required('A rua é obrigatória'),
  //         neighbourhood: Yup.string().required('O bairro é obrigatório'),
  //         city: Yup.string().required('A cidade é obrigatória'),
  //         state: Yup.string().required('O estado é obrigatório'),
  //       });

  //       await schema.validate(data, {
  //         abortEarly: false,
  //       });

  //       setLoading(true);

  //       const {
  //         postcode,
  //         road,
  //         neighbourhood,
  //         city,
  //         state,
  //         complement,
  //         reference,
  //         number,
  //       } = data;

  //       const uid = data.uid || v4();

  //       const parseData: any = {
  //         uid,
  //         postcode,
  //         road,
  //         number,
  //         neighbourhood,
  //         city,
  //         state,
  //         complement,
  //         reference,
  //       };

  //       const updateItems =
  //         client && client.addresses ? [...client.addresses] : [];

  //       if (index !== -1) {
  //         updateItems[index] = parseData;
  //       } else {
  //         updateItems.push(parseData);
  //       }

  //       await updateDoc(doc(DB, 'clients', id), {
  //         addresses: updateItems,
  //       });

  //       addToast({
  //         type: 'success',
  //         description: data.uid
  //           ? 'Endereço alterado com sucesso!'
  //           : 'Endereço cadastrado com sucesso!',
  //       });

  //       history.push(`/clients/addresses/${id}`);

  //       setLoading(false);
  //     } catch (err: any) {
  //       setLoading(false);
  //       if (err instanceof Yup.ValidationError) {
  //         const parseErrors = getValidationErrors(err);
  //         setErrors(parseErrors);
  //       }
  //     }
  //   },
  //   [data, addToast, history, client, index, id],
  // );

  return (
    <Dashboard containerMessage="col-xl-6 offset-xl-3 col-md-10 offset-md-1">
      <div className="row">
        <div className="col-xl-6 offset-xl-3 col-md-10 offset-md-1">
          <div className="nk-block nk-block-lg">
            <div className="nk-block-head">
              <div className="nk-block-head-content">
                <div className="nk-block-head-sub">
                  <Link className="back-to" to="/clients">
                    <em className="icon ni ni-arrow-left" />
                    <span>Voltar</span>
                  </Link>
                </div>
                <h4 className="title nk-block-title">
                  {itemId ? 'Editar Endereço' : 'Cadastrar Endereço'}
                </h4>
                <div className="nk-block-des">
                  <p>
                    Preencha o formulário abaixo e cadastre um novo endereço.
                  </p>
                </div>
              </div>
            </div>
            <div className="card card-bordered">
              <div className="card-inner">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="form-group mb-3">
                        <label className="form-label">Endereço</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={address}
                            onChange={event => setAddress(event.target.value)}
                          />
                          {errors.address && (
                            <span className="invalid">{errors.address}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">CEP</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control postcode"
                            defaultValue={data.postcode}
                            onChange={handleChangePostcode}
                          />
                          {errors.postcode && (
                            <span className="invalid">{errors.postcode}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Rua</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.road}
                            onChange={event => {
                              setData({
                                ...data,
                                road: event.target.value,
                              });
                            }}
                          />
                          {errors.road && (
                            <span className="invalid">{errors.road}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Número</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.number}
                            onChange={event => {
                              setData({
                                ...data,
                                number: event.target.value,
                              });
                            }}
                          />
                          {errors.number && (
                            <span className="invalid">{errors.number}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Bairro</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.neighbourhood}
                            onChange={event => {
                              setData({
                                ...data,
                                neighbourhood: event.target.value,
                              });
                            }}
                          />
                          {errors.neighbourhood && (
                            <span className="invalid">
                              {errors.neighbourhood}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Cidade</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.city}
                            onChange={event => {
                              setData({
                                ...data,
                                city: event.target.value,
                              });
                            }}
                          />
                          {errors.city && (
                            <span className="invalid">{errors.city}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Estado</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.state}
                            onChange={event => {
                              setData({
                                ...data,
                                state: event.target.value,
                              });
                            }}
                          />
                          {errors.state && (
                            <span className="invalid">{errors.state}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Complemento</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.complement}
                            onChange={event => {
                              setData({
                                ...data,
                                complement: event.target.value,
                              });
                            }}
                          />
                          {errors.complement && (
                            <span className="invalid">{errors.complement}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group mb-3">
                        <label className="form-label">
                          Ponto de Referência
                        </label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.reference}
                            onChange={event => {
                              setData({
                                ...data,
                                reference: event.target.value,
                              });
                            }}
                          />
                          {errors.reference && (
                            <span className="invalid">{errors.reference}</span>
                          )}
                        </div>
                      </div>
                    </div> */}

                    <div className="col-12">
                      <div className="form-group">
                        <button
                          type="submit"
                          className="btn btn-lg btn-primary"
                          disabled={loading}
                        >
                          {loading && (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            />
                          )}
                          {!loading && 'Salvar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {updateScript && (
        <Helmet>
          <script src={`${process.env.PUBLIC_URL}/assets/js/masks.js`} />
        </Helmet>
      )}
    </Dashboard>
  );
};

export default ClientAddressForm;
